import { TextField, Slider, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { WeeklyBalanceWidgetConfiguration } from './WeeklyBalanceWidgetConfiguration';
import { useDashboardContext } from '@/modules/dashboards/contexts/DashboardContextProvider';
import { useTranslation } from '@/lib';
import { LocationSelect } from '@/modules/locations/components/LocationSelect';
import AssetTypeSelect from '@/modules/asset-types/components/AssetTypeSelect';
import LocationHierarchyToggleButton from '@/modules/locations/components/LocationHierarchyToggleButton';

interface WeeklyBalanceWidgetConfigurationFormProps {
  configuration: WeeklyBalanceWidgetConfiguration;
  id: string;
}

const WeeklyBalanceWidgetConfigurationForm: FunctionComponent<WeeklyBalanceWidgetConfigurationFormProps> = ({ configuration, id }) => {
  const { updateWidgetConfiguration } = useDashboardContext();
  const { t } = useTranslation();

  const handleWeeksChange = (_event: Event, newValue: number | number[]) => {
    const weeks = Array.isArray(newValue) ? newValue[0] : newValue;
    updateWidgetConfiguration<WeeklyBalanceWidgetConfiguration>(id, {
      periodInWeeks: weeks,
    });
  };

  return (
    <div className="flex flex-col gap-y-4">
      <TextField
        label="Name"
        value={configuration.title}
        onChange={(e) => {
          updateWidgetConfiguration<WeeklyBalanceWidgetConfiguration>(id, {
            title: e.currentTarget.value,
          });
        }}
      />

      <LocationSelect
        label={t('location_select')}
        selectedLocationId={configuration.locationId}
        onSelected={(selectedLocation) => {
          if (selectedLocation) {
            updateWidgetConfiguration<WeeklyBalanceWidgetConfiguration>(id, {
              title: t('weekly_balance_widget_title', { locationName: selectedLocation.dto.name }),
              locationId: selectedLocation?.dto.id,
            });
          }
        }}
        helperText={t('location_select_description')}
      />

      <AssetTypeSelect
        selectedAssetTypeId={configuration.assetTypeId}
        onChange={(selectedAssetType) => {
          updateWidgetConfiguration<WeeklyBalanceWidgetConfiguration>(id, {
            assetTypeId: selectedAssetType?.id || 0,
          });
        }}
        helperText={t('asset_type_select_description')}
      />

      <div>
        <Typography variant="subtitle2" gutterBottom>
          {t('period')} ({configuration.periodInWeeks} {t('week', { count: configuration.periodInWeeks })})
        </Typography>
        <Slider
          value={configuration.periodInWeeks}
          onChange={handleWeeksChange}
          min={1}
          max={52}
          step={1}
          marks={[
            { value: 1, label: '1' },
            { value: 13, label: '13' },
            { value: 26, label: '26' },
            { value: 52, label: '52' },
          ]}
          valueLabelDisplay="auto"
        />
        <Typography variant="caption" color="text.secondary">
          {t('select_period_weeks_description')}
        </Typography>
      </div>

      <div>
        <div className="text-sm mb-2">{t('display_mode')}</div>
        <LocationHierarchyToggleButton
          displayMode={configuration.displayMode}
          onChange={(displayMode) => {
            updateWidgetConfiguration<WeeklyBalanceWidgetConfiguration>(id, {
              displayMode: displayMode,
            });
          }}
        />
      </div>
    </div>
  );
};

export default WeeklyBalanceWidgetConfigurationForm;
